import { isAxiosError } from 'axios'
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query'
import {
  productRecommendationListResponseSchema,
  productRecommendationMutationResponseSchema,
  productRecommendationAcceptResponseSchema,
  type ProductRecommendationConfidence,
  type ProductRecommendationReason,
  type ProductRecommendationResponse,
  type ProductRecommendationStatus,
  type UpdateMetadataFieldProposal,
} from '@artem-lex/common-server/productRecommendations/schemas'
import i18n from 'i18next'
import { toast } from 'sonner'
import { apiClient } from '@/shared/api/apiClient'
import type { SupportedLanguage } from '@/shared/config/languages'
import {
  llmChatSessionQueryKey,
  llmChatSessionsListQueryKey,
} from '@/shared/hooks/api/llmChat'
import {
  type LatestWorkflowForEntityResponse,
  getLatestWorkflowForEntity,
} from '@/shared/hooks/api/workflows'
import type { MachineryDirectiveCompliance } from './machineryDirectiveCompliance'

export const PRODUCTS_QUERY_KEY = 'products' as const

export const PRODUCT_INIT_WORKFLOW_TYPE = 'product_init' as const

type ProductMetadata = {
  name: string | null
  description: string | null
  yearOfManufacture: number | null
  productModel: string | null
}

export interface ProductInferredFact {
  id: string
  label: string
  value: string
  confidence?: 'low' | 'medium' | 'high'
  rationale?: string
  updatedAt: string
}

export interface ProductInferredFactsDocument {
  items: ProductInferredFact[]
}

export type ProductMetadataFieldKey = keyof ProductMetadata

export type ProductRecommendationKind = ProductRecommendationResponse['kind']

export type { ProductRecommendationStatus }
export type { ProductRecommendationReason }
export type { ProductRecommendationConfidence }

export type ProductRecommendationField = UpdateMetadataFieldProposal

export type ProductRecommendation = ProductRecommendationResponse

export type LexProduct = ProductMetadata & {
  id: string
  createdAt: string
  updatedAt: string | null
  deletedAt: string | null
  /** After first successful `product_init`; server guard for re-fill. */
  isInitialized: boolean
  inferredFactsJson: ProductInferredFactsDocument | null
  organizationId: string
  userId: string | null
}

/** All fields optional so the API can create a minimal placeholder (e.g. upload-first flow). */
export type CreateLexProductPayload = {
  name?: string | null
  description?: string | null
  associatedPartyId?: string | null
  yearOfManufacture?: number | null
  productModel?: string | null
}

export type UpdateLexProductPayload = {
  productId: string
  name?: string | null
  description?: string | null
  yearOfManufacture?: number | null
  productModel?: string | null
}

export type DeleteLexProductResponse = {
  message: string
}

export type UpdateProductInferredFactPayload = {
  productId: string
  factId: string
  label: string
  value: string
}

export type DeleteProductInferredFactPayload = {
  productId: string
  factId: string
}

export type GetProductRecommendationsPayload = {
  productId: string
  sessionId: string
  statuses?: ProductRecommendationStatus[]
}

export type ResolveProductRecommendationPayload = {
  productId: string
  recommendationId: string
}

export function useListProducts() {
  return useQuery<LexProduct[], Error>({
    queryKey: [PRODUCTS_QUERY_KEY],
    queryFn: async () => {
      const response = await apiClient.get<LexProduct[]>('/products')
      return response.data
    },
  })
}

export function useCreateProduct() {
  const queryClient = useQueryClient()

  return useMutation<LexProduct, Error, CreateLexProductPayload>({
    mutationFn: async (payload: CreateLexProductPayload) => {
      const body = Object.fromEntries(
        Object.entries(payload).filter(([, value]) => value !== undefined)
      ) as Record<string, unknown>
      const response = await apiClient.post<LexProduct>('/products', body)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PRODUCTS_QUERY_KEY] })
    },
  })
}

export function useUpdateProduct() {
  const queryClient = useQueryClient()

  return useMutation<LexProduct, Error, UpdateLexProductPayload>({
    mutationFn: async ({
      productId,
      name,
      description,
      yearOfManufacture,
      productModel,
    }: UpdateLexProductPayload) => {
      const response = await apiClient.patch<LexProduct>(
        `/products/${productId}`,
        {
          name,
          description,
          yearOfManufacture,
          productModel:
            productModel != null && productModel.trim() !== ''
              ? productModel.trim()
              : null,
        }
      )
      return response.data
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: [PRODUCTS_QUERY_KEY] })
      void queryClient.invalidateQueries({
        queryKey: [PRODUCTS_QUERY_KEY, variables.productId],
      })
    },
  })
}

export function useDeleteProduct() {
  const queryClient = useQueryClient()

  return useMutation<DeleteLexProductResponse, Error, string>({
    mutationFn: async (productId: string) => {
      const response = await apiClient.delete<DeleteLexProductResponse>(
        `/products/${productId}`
      )
      return response.data
    },
    onSuccess: (_data, productId) => {
      void queryClient.invalidateQueries({ queryKey: [PRODUCTS_QUERY_KEY] })
      void queryClient.invalidateQueries({
        queryKey: [PRODUCTS_QUERY_KEY, productId],
      })
    },
  })
}

export function useUpdateProductInferredFact() {
  const queryClient = useQueryClient()

  return useMutation<
    ProductInferredFact,
    Error,
    UpdateProductInferredFactPayload
  >({
    mutationFn: async ({ productId, factId, label, value }) => {
      const response = await apiClient.patch<ProductInferredFact>(
        `/products/${productId}/inferred-facts/${factId}`,
        {
          label,
          value,
        }
      )
      return response.data
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: [PRODUCTS_QUERY_KEY, variables.productId],
      })
    },
  })
}

export function useDeleteProductInferredFact() {
  const queryClient = useQueryClient()

  return useMutation<void, Error, DeleteProductInferredFactPayload>({
    mutationFn: async ({ productId, factId }) => {
      await apiClient.delete(`/products/${productId}/inferred-facts/${factId}`)
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: [PRODUCTS_QUERY_KEY, variables.productId],
      })
    },
  })
}

export function useProductRecommendations(
  productId: string,
  sessionId: string,
  statuses?: ProductRecommendationStatus[]
) {
  return useQuery<ProductRecommendation[], Error>({
    queryKey: productRecommendationsQueryKey(
      productId,
      sessionId,
      statuses ?? []
    ),
    queryFn: async () => {
      const response = await apiClient.get(
        `/products/${productId}/recommendations`,
        {
          params: {
            sessionId,
            ...(statuses !== undefined && statuses.length > 0
              ? { status: statuses }
              : {}),
          },
        }
      )
      return productRecommendationListResponseSchema.parse(response.data)
        .recommendations
    },
    enabled: Boolean(productId) && Boolean(sessionId),
  })
}

function productRecommendationsQueryKey(
  productId: string,
  sessionId: string,
  statuses: ProductRecommendationStatus[] = []
) {
  return [
    PRODUCTS_QUERY_KEY,
    productId,
    'recommendations',
    sessionId,
    statuses,
  ] as const
}

/**
 * Merges a server-pushed list of recommendations into the TanStack Query cache
 * without triggering a network request. Existing entries (by id) are preserved
 * so that any status updates from accept/dismiss mutations are not overwritten.
 *
 * Called from `onData` when a `data-lex-product-recommendations-sync` SSE event
 * arrives, before the corresponding tool-output-available event renders the card.
 */
export function setProductRecommendationsCache(
  queryClient: QueryClient,
  productId: string,
  sessionId: string,
  incoming: ProductRecommendation[]
): void {
  queryClient.setQueryData<ProductRecommendation[]>(
    productRecommendationsQueryKey(productId, sessionId),
    (previous) => {
      if (previous === undefined) {
        return incoming
      }
      const existingIds = new Set(previous.map((rec) => rec.id))
      const newOnes = incoming.filter((rec) => !existingIds.has(rec.id))
      if (newOnes.length === 0) {
        return previous
      }
      return [...previous, ...newOnes]
    }
  )
}

function patchAcceptedRecommendationInCache(
  queryClient: QueryClient,
  productId: string,
  recommendation: ProductRecommendation
): void {
  queryClient.setQueryData<ProductRecommendation[]>(
    productRecommendationsQueryKey(productId, recommendation.sessionId),
    (previous) => {
      if (previous === undefined) {
        return previous
      }
      return previous.map((item) =>
        item.id === recommendation.id ? recommendation : item
      )
    }
  )
}

/** Optimistic status update while chat resolve+continue stream runs. */
export function patchRecommendationStatusInCache(
  queryClient: QueryClient,
  productId: string,
  sessionId: string,
  recommendationId: string,
  status: ProductRecommendationStatus
): void {
  queryClient.setQueryData<ProductRecommendation[]>(
    productRecommendationsQueryKey(productId, sessionId),
    (previous) => {
      if (previous === undefined) {
        return previous
      }
      return previous.map((item) =>
        item.id === recommendationId ? { ...item, status } : item
      )
    }
  )
}

export function invalidateProductRecommendationQueries(
  queryClient: QueryClient,
  productId: string,
  sessionId?: string
): void {
  if (sessionId !== undefined) {
    void queryClient.invalidateQueries({
      queryKey: [PRODUCTS_QUERY_KEY, productId, 'recommendations', sessionId],
    })
    void queryClient.invalidateQueries({
      queryKey: llmChatSessionQueryKey(sessionId),
    })
  } else {
    void queryClient.invalidateQueries({
      queryKey: [PRODUCTS_QUERY_KEY, productId, 'recommendations'],
    })
  }
  void queryClient.invalidateQueries({
    queryKey: [PRODUCTS_QUERY_KEY, productId],
  })
  void queryClient.invalidateQueries({
    queryKey: [PRODUCTS_QUERY_KEY],
  })
  void queryClient.invalidateQueries({
    queryKey: llmChatSessionsListQueryKey(),
  })
}

export function useAcceptProductRecommendation() {
  const queryClient = useQueryClient()

  return useMutation<
    {
      applied: boolean
      recommendation: ProductRecommendation
      chatContinuationEligible: boolean
    },
    Error,
    ResolveProductRecommendationPayload
  >({
    mutationFn: async ({ productId, recommendationId }) => {
      const response = await apiClient.post(
        `/products/${productId}/recommendations/${recommendationId}/accept`
      )
      return productRecommendationAcceptResponseSchema.parse(response.data)
    },
    onSuccess: ({ applied, recommendation }, variables) => {
      if (applied) {
        patchAcceptedRecommendationInCache(
          queryClient,
          variables.productId,
          recommendation
        )
      }
      invalidateProductRecommendationQueries(
        queryClient,
        variables.productId,
        recommendation.sessionId
      )
      if (applied) {
        toast.success(
          i18n.t('recommendations.feedback.saved', { ns: 'llmChat' })
        )
        return
      }
      if (recommendation.status === 'stale') {
        toast.warning(
          i18n.t('recommendations.feedback.stale', { ns: 'llmChat' })
        )
      }
    },
    onError: (_error, variables) => {
      toast.error(
        i18n.t('recommendations.errors.actionFailed', { ns: 'llmChat' })
      )
      invalidateProductRecommendationQueries(queryClient, variables.productId)
    },
  })
}

export function useDismissProductRecommendation() {
  const queryClient = useQueryClient()

  return useMutation<
    {
      recommendation: ProductRecommendation
      chatContinuationEligible: boolean
    },
    Error,
    ResolveProductRecommendationPayload
  >({
    mutationFn: async ({ productId, recommendationId }) => {
      const response = await apiClient.post(
        `/products/${productId}/recommendations/${recommendationId}/dismiss`
      )
      return productRecommendationMutationResponseSchema.parse(response.data)
    },
    onSuccess: ({ recommendation }, variables) => {
      invalidateProductRecommendationQueries(
        queryClient,
        variables.productId,
        recommendation.sessionId
      )
    },
    onError: (_error, variables) => {
      toast.error(
        i18n.t('recommendations.errors.actionFailed', { ns: 'llmChat' })
      )
      invalidateProductRecommendationQueries(queryClient, variables.productId)
    },
  })
}

export function useGetProduct(productId: string) {
  return useQuery<LexProduct, Error>({
    queryKey: [PRODUCTS_QUERY_KEY, productId],
    queryFn: async () => {
      const response = await apiClient.get<LexProduct>(`/products/${productId}`)
      return response.data
    },
    enabled: Boolean(productId),
    refetchInterval(query) {
      const data = query.state.data
      if (!data) {
        return false
      }
      if (!data.isInitialized) {
        return 2000
      }
      return false
    },
  })
}

export function useGetProductMachineryDirectiveCompliance(
  productId: string,
  enabled = true
) {
  return useQuery<MachineryDirectiveCompliance | null, Error>({
    queryKey: [PRODUCTS_QUERY_KEY, productId, 'machinery-directive'],
    queryFn: async () => {
      try {
        const response = await apiClient.get<MachineryDirectiveCompliance>(
          `/products/${productId}/machinery-directive-compliance`
        )
        return response.data
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 404) {
          return null
        }
        throw error
      }
    },
    enabled,
  })
}

export function useCreateProductMachineryDirectiveCompliance(
  productId: string
) {
  const queryClient = useQueryClient()

  return useMutation<
    MachineryDirectiveCompliance,
    Error,
    { contentLanguage: SupportedLanguage }
  >({
    mutationFn: async (payload) => {
      const response = await apiClient.post<MachineryDirectiveCompliance>(
        `/products/${productId}/machinery-directive-compliance`,
        payload
      )
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [PRODUCTS_QUERY_KEY, productId, 'machinery-directive'],
      })
    },
  })
}

export function useGetProductComplianceStudios(
  productId: string,
  enabled = true
) {
  const {
    data: machineryDirectiveCompliance,
    isLoading: isLoadingMachineryDirective,
    isError: isErrorMachineryDirective,
    refetch: refetchMachineryDirective,
  } = useGetProductMachineryDirectiveCompliance(productId, enabled)

  return {
    machineryDirectiveCompliance,
    isLoading: isLoadingMachineryDirective,
    isError: isErrorMachineryDirective,
    isLoadingMachineryDirective,
    isErrorMachineryDirective,
    refetchMachineryDirective,
  }
}

/**
 * Latest persisted `product_init` workflow for polling step / LLM events on the UI.
 * Poll while mounted — do not stop when a run hits `completed` early (e.g. batch skip);
 * initialization panel unmounts once `product.isInitialized` is true.
 */
export function useProductInitWorkflowLatest(productId: string | undefined) {
  return useQuery<LatestWorkflowForEntityResponse, Error>({
    queryKey: [PRODUCTS_QUERY_KEY, productId, 'product-init-workflow-latest'],
    queryFn: async () =>
      getLatestWorkflowForEntity(
        'product',
        productId!,
        PRODUCT_INIT_WORKFLOW_TYPE
      ),
    enabled: Boolean(productId),
    refetchInterval(query) {
      const status = query.state.data?.status
      if (status === 'failed') {
        return false
      }
      return 2000
    },
    staleTime: 0,
  })
}

export function useRetryProductInitialization(productId: string) {
  const queryClient = useQueryClient()

  return useMutation<{ accepted: boolean }, Error>({
    mutationFn: async () => {
      const response = await apiClient.post<{ accepted: boolean }>(
        `/products/${productId}/initialize`
      )
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [
          PRODUCTS_QUERY_KEY,
          productId,
          'product-init-workflow-latest',
        ],
      })
      void queryClient.invalidateQueries({
        queryKey: [PRODUCTS_QUERY_KEY, productId],
      })
    },
  })
}
